import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Check, Pencil, Phone, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminShell, AdminSection, AdminCard } from "@/components/admin/shell";
import { Button } from "@/components/ui/button";
import { normalizeTeamNumber, type TeamNumber } from "@/lib/team-numbers";
import { getTeamNumbersFn, setTeamNumbersFn } from "@/lib/site-settings/team-numbers.functions";

export const Route = createFileRoute("/admin/configuracoes")({
  head: () => ({
    meta: [
      { title: "Admin — Verificador de números" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/configuracoes">
      <ConfigPage />
    </AdminGate>
  ),
});

function fmt(n: string) {
  const d = n.replace(/^55/, "");
  return `+55 (${d.slice(0, 2)}) ${d.slice(2, d.length - 4)}-${d.slice(-4)}`;
}

function ConfigPage() {
  return (
    <AdminShell
      title="Verificador de números"
      description="Números oficiais do time exibidos nos verificadores do site"
      icon={<Phone className="h-5 w-5" />}
    >
      <AdminSection label="Números do time">
        <TeamNumbersEditor />
      </AdminSection>
    </AdminShell>
  );
}

function TeamNumbersEditor() {
  const load = useServerFn(getTeamNumbersFn);
  const save = useServerFn(setTeamNumbersFn);
  const q = useQuery({ queryKey: ["admin", "team-numbers"], queryFn: () => load() });
  const [list, setList] = useState<TeamNumber[]>([]);
  const [nome, setNome] = useState("");
  const [numero, setNumero] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");
  const [editNumero, setEditNumero] = useState("");

  const confirmEdit = (i: number) => {
    const n = normalizeTeamNumber(editNumero);
    if (!/^55\d{10,11}$/.test(n)) return toast.error("Número inválido. Use DDD + número.");
    if (list.some((x, j) => j !== i && x.numero === n)) return toast.error("Esse número já está na lista.");
    const next = [...list];
    next[i] = { numero: n, nome: editNome.trim() };
    setList(next);
    setEditing(null);
    setDirty(true);
  };

  useEffect(() => {
    if (q.data) setList(q.data);
  }, [q.data]);

  const add = () => {
    const n = normalizeTeamNumber(numero);
    if (!/^55\d{10,11}$/.test(n)) return toast.error("Número inválido. Use DDD + número.");
    if (list.some((x) => x.numero === n)) return toast.error("Esse número já está na lista.");
    setList([...list, { numero: n, nome: nome.trim() }]);
    setNome("");
    setNumero("");
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await save({ data: { numbers: list } });
      toast.success("Verificador salvo. As páginas atualizam em até 1 minuto.");
      setDirty(false);
      void q.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminCard>
      <div className="space-y-6 p-6">
        <p className="text-sm text-white/60">
          Vale para todos os quadros "Verifique o número do nosso time" do site. {list.length} números.
        </p>
        {q.isLoading ? (
          <p className="text-sm text-white/60">Carregando…</p>
        ) : (
          <ul className="divide-y divide-white/10 rounded-xl border border-white/10">
            {list.map((n, i) =>
              editing === i ? (
                <li key={n.numero} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                  <input
                    value={editNome}
                    onChange={(e) => setEditNome(e.target.value)}
                    placeholder="Nome"
                    className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
                  />
                  <input
                    value={editNumero}
                    onChange={(e) => setEditNumero(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && confirmEdit(i)}
                    inputMode="tel"
                    className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
                  />
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" aria-label="Confirmar edição" onClick={() => confirmEdit(i)}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" aria-label="Cancelar edição" onClick={() => setEditing(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ) : (
                <li key={n.numero} className="flex items-center gap-4 px-4 py-3">
                  <Phone className="h-4 w-4 shrink-0 text-white/40" />
                  <span className="min-w-0 flex-1 truncate text-sm text-white">{n.nome || "Sem nome"}</span>
                  <span className="font-mono text-sm text-white/80">{fmt(n.numero)}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Editar número"
                    onClick={() => {
                      setEditing(i);
                      setEditNome(n.nome);
                      setEditNumero(fmt(n.numero));
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Excluir número"
                    onClick={() => {
                      setList(list.filter((_, j) => j !== i));
                      setEditing(null);
                      setDirty(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ),
            )}
          </ul>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Nome (ex.: Ingrid Chip 3)"
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
          />
          <input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="(11) 99999-9999"
            inputMode="tel"
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
          />
          <Button variant="outline" onClick={add}>
            <Plus className="mr-2 h-4 w-4" /> Adicionar
          </Button>
        </div>
        <Button onClick={onSave} disabled={saving || !dirty} className="w-full sm:w-auto">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Salvando…" : "Salvar verificador"}
        </Button>
      </div>
    </AdminCard>
  );
}
